import IconExpandMore from '@fortuneo/ui-icons/general/IconExpandMore'
import clsx from 'clsx'
import React, { type PropsWithChildren, useEffect, useState } from 'react'

type Option = {
  label: string
  value: string
  group?: string
}

export type DropdownProps = {
  disabled?: boolean
  emptyOption?: boolean
  fullWidth?: boolean
  helper?: string
  label?: string
  onChange?: (
    event: React.ChangeEvent<HTMLSelectElement> | any
  ) => void | ((event: Option[]) => void) | undefined
  placeholder?: string
  required?: boolean
  validation?: 'error' | 'success' | 'warning'
  value?: any
} & React.HTMLAttributes<HTMLElement>

export const Dropdown: React.FC<PropsWithChildren<DropdownProps>> = ({
  children,
  disabled = false,
  emptyOption = true,
  fullWidth = false,
  helper,
  label,
  onChange,
  placeholder,
  required = false,
  validation,
  value,
  className,
  ...props
}) => {
  const [currentValue, setCurrentValue] = useState(value || '')

  useEffect(() => {
    setCurrentValue(value)
  }, [value])

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentValue(event.target.value)
    if (onChange) {
      onChange(event)
    }
  }

  return (
    <div
      className={clsx(
        'relative z-[1] min-w-[280px]',
        fullWidth ? 'flex' : 'inline-block',
        disabled && 'pointer-events-none opacity-50',
        className
      )}
      {...props}
    >
      <div
        className={clsx(
          'relative block w-full rounded-[5px] border border-solid bg-white-50',
          'focus-within:outline focus-within:outline-3 focus-within:outline-offset-4 focus-within:outline-border-focus',
          validation === 'error'
            ? 'border-theme-error-main'
            : 'border-grey-200',
          disabled
            ? 'border-grey-200'
            : 'hover:ring-2 hover:ring-grey-600 hover:ring-offset-0'
        )}
      >
        {label && (
          <label
            className={clsx(
              'pointer-events-none absolute left-3 right-10 z-[2] overflow-hidden text-ellipsis whitespace-nowrap text-085 transition-all',
              emptyOption && !currentValue && !placeholder
                ? 'top-[22px]'
                : 'top-[9px]'
            )}
          >
            {label}
          </label>
        )}

        <select
          disabled={disabled}
          required={required}
          value={currentValue}
          className={clsx(
            'relative z-[1] h-16 w-full appearance-none overflow-hidden text-ellipsis whitespace-nowrap border-0 bg-transparent bg-none pb-3 pl-3 pr-10 pt-8 font-medium focus-visible:outline-none',
            disabled ? 'cursor-default' : 'cursor-pointer',
            fullWidth ? 'none' : 'max-w-[300px]'
          )}
          onChange={handleChange}
        >
          {placeholder && (
            <option disabled={true} value="">
              {placeholder}
            </option>
          )}
          {emptyOption && <option label="" value="" />}
          {children}
        </select>

        <div className="absolute right-[11px] top-1/2 flex -translate-y-1/2">
          <IconExpandMore className="fill-grey-900" size={20} />
        </div>
      </div>
      {helper && (
        <div
          className={clsx(
            'my-2 text-075',
            validation && 'text-theme-error-main'
          )}
        >
          {helper}
        </div>
      )}
    </div>
  )
}
